import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { SimilarListing } from '@/src/components/features/property/hooks/useSimilarListings';

export const STATIC_CATEGORIES = [
  'Distance',
  'Pets',
  'Laundry',
  'Security',
  'A/C',
  'Dishwasher',
  'Parking',
  'Gym',
  'Pool',
] as const;

export type StaticCategory = (typeof STATIC_CATEGORIES)[number];

export type BedroomLabel = 'Studio' | '1 bed' | '2 beds' | '3 beds' | '4+ beds';

/**
 * Listing name precedence shared by the comparison table and the
 * server-rendered SEO links (SimilarListingsSeoLinks): displayName → title → name.
 */
export function getSimilarListingName(listing: {
  displayName?: string;
  title?: string;
  name?: string;
}): string | undefined {
  return listing.displayName || listing.title || listing.name || undefined;
}

const BEDROOM_MAP: Record<number, BedroomLabel> = {
  0: 'Studio',
  1: '1 bed',
  2: '2 beds',
  3: '3 beds',
  4: '4+ beds',
};

export type CellValue = string | boolean | null;

/**
 * Comparison table accepts the current listing (full PropertyDetailListing) and
 * a list of similar listings (lighter SimilarListing shape). Both end up rendered
 * as columns; the structural fields they share are read through this widened type.
 */
type ComparisonListing = (PropertyDetailListing | SimilarListing) & {
  active?: boolean;
  isAllowedToRetain?: boolean;
  uriV2?: string;
  uriMalone?: string;
  uriBuilding?: string;
  displayName?: string;
  name?: string;
  title?: string;
  hasTotalMonthlyPrice?: boolean;
};

export type ComparisonRow = {
  /** Listing this column represents (current listing first, then similar listings). */
  listing: ComparisonListing;
  title: string;
  image: string;
  link: string | null;
  /** Bedroom-keyed prices (e.g. "Studio" -> "$1,200+"). */
  prices: Partial<Record<BedroomLabel, string | null>>;
  /** Static amenity rows. */
  amenities: Record<StaticCategory, CellValue>;
};

const canRetainCurrentListingDetails = (listing: ComparisonListing): boolean =>
  listing.active !== false || listing.isAllowedToRetain !== false;

// Inlined haversine — vercel doesn't currently expose a shared util for this.
const haversineMiles = (lat1: number, lon1: number, lat2: number, lon2: number): number | null => {
  if (!Number.isFinite(lat1) || !Number.isFinite(lon1) || !Number.isFinite(lat2) || !Number.isFinite(lon2)) {
    return null;
  }
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R_MILES = 3958.7613;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R_MILES * c;
};

const getDistanceLabel = (
  current: ComparisonListing,
  similar: ComparisonListing,
  formatDistance: (miles: number) => string,
): string | null => {
  const cGeo = (current as { geo?: { lat?: number; lon?: number } }).geo;
  const sGeo = (similar as { geo?: { lat?: number; lon?: number } }).geo;
  if (!cGeo || !sGeo) return null;
  if (cGeo.lat == null || cGeo.lon == null || sGeo.lat == null || sGeo.lon == null) return null;
  if (cGeo.lat === sGeo.lat && cGeo.lon === sGeo.lon) return null;

  const miles = haversineMiles(cGeo.lat, cGeo.lon, sGeo.lat, sGeo.lon);
  if (miles == null || miles < 0.1) return null;
  return formatDistance(Number(miles.toFixed(1)));
};

const asCurrency = (value: number): string => `$${Math.round(value).toLocaleString('en-US')}`;

/**
 * Returns the bedroom labels covering the min/max range across all listings.
 * Anything 4+ collapses into a single "4+ beds" bucket.
 */
export const getBedroomLabels = (listings: ReadonlyArray<ComparisonListing>): Array<BedroomLabel> => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  listings.forEach((listing) => {
    const minMax = (listing as { listingMinMaxPriceBeds?: { minBeds?: number; maxBeds?: number } })
      .listingMinMaxPriceBeds;
    if (minMax && minMax.minBeds != null && minMax.maxBeds != null) {
      if (minMax.minBeds < min) min = minMax.minBeds;
      if (minMax.maxBeds > max) max = minMax.maxBeds;
    }

    getPricingModels(listing).forEach((model) => {
      if (model.numBeds < min) min = model.numBeds;
      if (model.numBeds > max) max = model.numBeds;
    });
  });

  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];

  const seen = new Set<BedroomLabel>();
  const ordered: Array<BedroomLabel> = [];
  for (let n = min; n <= max; n++) {
    const label = BEDROOM_MAP[n] ?? BEDROOM_MAP[4];
    if (!seen.has(label)) {
      seen.add(label);
      ordered.push(label);
    }
  }
  return ordered;
};

type PricingModel = { numBeds: number; lowPrice?: number | null; highPrice?: number | null };

type RawPricingModel = {
  numBeds?: number;
  beds?: number | string;
  low?: number | string | null;
  high?: number | string | null;
  lowPrice?: number | string | null;
  highPrice?: number | string | null;
};

const toFiniteNumber = (value: number | string | null | undefined): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getPricingModels = (listing: ComparisonListing): Array<PricingModel> => {
  const withPricing = listing as {
    modelsAndPricing?: Array<RawPricingModel>;
    models?: Array<RawPricingModel>;
  };
  return (withPricing.modelsAndPricing ?? withPricing.models ?? []).flatMap((model): Array<PricingModel> => {
    const numBeds = toFiniteNumber(model.numBeds ?? model.beds);
    if (numBeds == null) return [];
    return [
      {
        numBeds,
        lowPrice: toFiniteNumber(model.lowPrice ?? model.low),
        highPrice: toFiniteNumber(model.highPrice ?? model.high),
      },
    ];
  });
};

const getPricesByBedroom = (
  listing: ComparisonListing,
  labels: ReadonlyArray<BedroomLabel>,
  availableLabel: string,
): Partial<Record<BedroomLabel, string | null>> => {
  const result: Partial<Record<BedroomLabel, string | null>> = {};
  labels.forEach((label) => {
    result[label] = null;
  });

  const models = getPricingModels(listing);

  models.forEach((model) => {
    const label = BEDROOM_MAP[model.numBeds] ?? BEDROOM_MAP[4];
    if (!labels.includes(label)) return;

    const { lowPrice, highPrice } = model;
    if (lowPrice == null && highPrice == null) {
      result[label] = availableLabel;
      return;
    }

    const low = asCurrency(lowPrice ?? 0);
    if (highPrice == null || lowPrice === highPrice) {
      result[label] = low;
    } else {
      result[label] = `${low}+`;
    }
  });

  return result;
};

const getAmenityRow = (listing: ComparisonListing): Record<StaticCategory, CellValue> => {
  const amenities = (listing as { amenities?: { condensedAmenities?: Record<string, unknown> } }).amenities
    ?.condensedAmenities;
  const get = (key: string): CellValue => {
    const value = amenities?.[key];
    if (value == null) return null;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value;
    return null;
  };
  return {
    Distance: null,
    Pets: get('pets'),
    Laundry: get('laundry'),
    Security: get('security'),
    'A/C': get('airConditioning'),
    Dishwasher: get('dishwasher'),
    Parking: get('parking'),
    Gym: get('gym'),
    Pool: get('pool'),
  };
};

const getListingPhoto = (listing: ComparisonListing): string => {
  const withPhotos = listing as {
    photos?: Array<{ url?: string }>;
    medPhotoUrl?: string;
    medPhotoUrls?: Array<string>;
    photoUrl?: string;
    previewPhoto?: { url?: string };
    previewPhotoMed?: { url?: string };
    thumbPhoto?: { url?: string };
    thumbPhotoUrl?: string;
  };
  return (
    withPhotos.photos?.[0]?.url ||
    withPhotos.medPhotoUrl ||
    withPhotos.medPhotoUrls?.[0] ||
    withPhotos.previewPhoto?.url ||
    withPhotos.previewPhotoMed?.url ||
    withPhotos.photoUrl ||
    withPhotos.thumbPhoto?.url ||
    withPhotos.thumbPhotoUrl ||
    ''
  );
};

const AMENITY_CATEGORIES = STATIC_CATEGORIES.filter((category) => category !== 'Distance');

const hasAmenityData = (row: ComparisonRow): boolean =>
  AMENITY_CATEGORIES.some((category) => row.amenities[category] != null);

export const buildComparisonRows = (
  currentListing: PropertyDetailListing,
  similarListings: ReadonlyArray<SimilarListing>,
  {
    formatDistance,
    availableLabel,
  }: {
    formatDistance: (miles: number) => string;
    availableLabel: string;
  },
): {
  rows: Array<ComparisonRow>;
  bedroomLabels: Array<BedroomLabel>;
  hasCurrentListingColumn: boolean;
  /** Distance always; the amenity rows only when a similar listing carries amenity data (the v3 card sends none). */
  staticCategories: Array<StaticCategory>;
} => {
  const current = currentListing as ComparisonListing;
  const hasCurrentListingColumn = canRetainCurrentListingDetails(current);
  const visibleListings: Array<ComparisonListing> = hasCurrentListingColumn
    ? [current, ...(similarListings as Array<ComparisonListing>)]
    : (similarListings as Array<ComparisonListing>);
  const bedroomLabels = getBedroomLabels(visibleListings);

  const rows: Array<ComparisonRow> = visibleListings.map((listing, index) => {
    const isCurrent = hasCurrentListingColumn && index === 0;
    const hideStreet = (listing as { address?: { hideStreet?: boolean } }).address?.hideStreet === true;
    const title = hideStreet
      ? '(Undisclosed Address)'
      : getSimilarListingName(listing) || (listing as { address?: { street?: string } }).address?.street || '';
    const image = getListingPhoto(listing);
    const link = isCurrent ? null : listing.uriV2 || listing.uriMalone || listing.uriBuilding || null;

    const amenities = getAmenityRow(listing);
    if (hasCurrentListingColumn && !isCurrent) {
      amenities.Distance = getDistanceLabel(current, listing, formatDistance);
    }

    return {
      listing,
      title,
      image,
      link,
      prices: getPricesByBedroom(listing, bedroomLabels, availableLabel),
      amenities,
    };
  });

  const similarRows = hasCurrentListingColumn ? rows.slice(1) : rows;
  const staticCategories: Array<StaticCategory> = similarRows.some(hasAmenityData)
    ? [...STATIC_CATEGORIES]
    : ['Distance'];

  return { rows, bedroomLabels, hasCurrentListingColumn, staticCategories };
};

export const formatCellValue = (value: CellValue, placeholder = '—'): string => {
  if (value == null) return placeholder;
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return value;
};
